import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { IEpisodeDetailsDialogData, IEpisodeDetailsDialogViewData, IEpisodeDetailsErrors, IEpisodeError, IEpisodeQualityOption, IEpisodeThumbnailInputViewData } from '../../../../../core/utils/episode-details-utils';
import { EpisodeQuality, ExtendedProgramGuideService, IInputEpisode } from 'src/app/core/services/extended-program-guide.service';
import Debounce from 'src/app/core/utils/Debounce';
import { EpisodeThumbnailInputComponent } from '../../episode-thumbnail-input/episode-thumbnail-input.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-episode-general-tab',
    templateUrl: './episode-general-tab.component.html',
    styleUrls: ['./episode-general-tab.component.scss'],
    standalone: false
})
export class EpisodeGeneralTabComponent implements AfterViewInit,OnInit {

  /**
   * Element where the thumbnail will be rendered
   */
  @ViewChild('titleThumbnailInput')
  private titleThumbnailInput: EpisodeThumbnailInputComponent;

  /**
   * Element where the thumbnail will be rendered
   */
  @ViewChild('showThumbnailInput')
  private showThumbnailInput: EpisodeThumbnailInputComponent;

  private readonly validationDebounce = new Debounce(100);

  public isUpdatingViewData = false;

  public extraDetailsList = [
    {
      label: 'Season',
      value: false,
    },
    {
      label: 'Episode',
      value: false,
    },
    {
      label: 'Year',
      value: false,
    },
    {
      label: 'League',
      value: false,
    },
    {
      label: 'Cast',
      value: false,
    },
    {
      label: 'Host',
      value: false,
    },
  ];

  public readonly viewData: IEpisodeDetailsDialogViewData = {
    viewerRating: 0,
    quality: 0,
    title: '',
    formattedRecurrence: null,
    showName: '',
    description: '',
    showDetails: {
      seasonNumber: '',
      episodeNumber: '',
      year: '',
      league: '',
      showType: '',
      closedCaptions: false,
      subtitles: false,
      cast: '',
      host: ''
    },
    thumbnails: {
      title: null,
      show: null
    },
  };

  public errors: IEpisodeDetailsErrors = {
    viewerRating: null,
    title: null,
    runsIndefinitely: null,
    description: null,
    showName: null,
    quality: null,
    duration: null,
    start: {
      date: null,
      time: null
    },
    end: {
      date: null,
      time: null
    }
  };

  public quality: IEpisodeQualityOption[] = [
    {
      label: 'Standard',
      value: EpisodeQuality.Standard
    },
    {
      label: 'HD',
      value: EpisodeQuality.HD
    }
  ];

  public raitings = [
    {
      label: '+14',
      value: 14
    },
    {
      label: '+18',
      value: 18
    }
  ];

  @Input() public data: IEpisodeDetailsDialogData;
  @Input() public showErrors: boolean;
  @Output() viewDataUpdateEvent = new EventEmitter<IEpisodeDetailsDialogViewData>();
  @Output() errorUpdateEvent = new EventEmitter<IEpisodeDetailsErrors>();
  @Output() thumbnailTitleUpdateEvent = new EventEmitter<boolean>();
  @Output() thumbnailCardUpdateEvent = new EventEmitter<boolean>();
 
  public constructor(
    private readonly changeDetectionRef: ChangeDetectorRef,
    private readonly matSnackBar: MatSnackBar,
    private readonly epgService: ExtendedProgramGuideService,
  ){}

  public ngOnInit(): void {
  }

  public ngAfterViewInit(): void {
    this.triggerSetViewData(this.data.episode);
  }

  private triggerSetViewData(value: IInputEpisode) {
    if (this.isUpdatingViewData) {
      return;
    }
    this.isUpdatingViewData = true;
    this.setViewData(value)
      .catch(
        (err): IEpisodeError => ({
          message: `${err}`
        })
      )
      .then((err) => {
        if (err !== null) {
          this.matSnackBar.open(
            `Failed to process episode information: ${err.message}`,
            'Ok'
          );
        }
      })
      .finally(() => {
        this.isUpdatingViewData = false;
      });
  }

   /**
   * Update component `viewData` property from the input episode
   * @param value Input episode
   */
   private async setViewData(value: IInputEpisode) {
    /**
     * Quality
     */
    this.viewData.quality = value.quality;

    /**
     * Description
     */
    this.viewData.description = value.description;

    /**
     * Viewer rating
     */
    this.viewData.viewerRating = parseInt(value.viewer_rating, 10);

    /**
     * Closed captions
     */
    this.viewData.showDetails.closedCaptions = value.options.hasClosedCaptions;

    /**
     * Title
     */
    this.viewData.title = value.title;

    /**
     * name
     */
    this.viewData.showName = value.name;

    /**
     * Set IInputEpisode thumbnail data to `this.viewData`
     */
    for (const thumbnail of [
      {
        thumbnailName: 'show',
        type: 'show_card',
        elementRef: this.showThumbnailInput
      },
      {
        thumbnailName: 'title',
        type: 'title_card',
        elementRef: this.titleThumbnailInput
      }
    ] as const) {
      const valueProp =
        thumbnail.thumbnailName === 'show'
          ? 'show_thumbnail_url'
          : 'title_thumbnail_url';
      const thumbnailUrl = value[valueProp];
      const elementRef = thumbnail.elementRef;
      /**
       * In case the thumbnail element ref is not available, return an error
       */
      if (!elementRef) {
        // this.viewData.thumbnails[thumbnail.thumbnailName] = null;
        // continue;
        return {
          message: 'Internal: Failed to find thumbnail element'
        };
      }
      /**
       * In case no thumbnail is set, set the thumbnail to null
       */
      if (thumbnailUrl === null) {
        const result = await elementRef.setThumbnail(null);
        if (result !== null) {
          return result;
        }
        this.viewData.thumbnails[thumbnail.thumbnailName] = null;
        continue;
      }
      let downloadedThumbnail: File;
      try {
        let myBlob = await firstValueFrom(
          this.epgService.downloadEpisodeThumbnail(value.id, thumbnail.type)
        );
        downloadedThumbnail = new File([myBlob], "name");
      } catch (reason) {
        return {
          message: `Failed to download thumbnail: ${reason}`
        };
      }
      const err = await elementRef.setThumbnail(downloadedThumbnail);
      if (err !== null) {
        return err;
      }
      this.viewData.thumbnails[thumbnail.thumbnailName] = elementRef.thumbnail;
    }

    return this.triggerUpdate().then(() => null);
  }

  public onViewDataChange() {   
    this.triggerUpdate();
  }

  /**
   * This should be called whenever a view data is changed, so we can disable the save button,
   * and only enable it if everything is gtg.
   *
   * The promise returned will only be resolved after the current debounce timer is fired. Meaning,
   * if it is cleared, the promise will never be resolved.
   */
  private triggerUpdate() {
    /**
     * disable the save button until the update is completed
     */
    // this.dialogOptions.saveButton.disable = true;
    return this.validationDebounce.run(() => {
      this.update();
      this.changeDetectionRef.detectChanges();
    });
  }

   private update() {
    for (const validation of [
      {
        name: 'title',
        value: this.viewData.title
      },
      {
        name: 'showName',
        value: this.viewData.showName
      },
      {
        name: 'description',
        value: this.viewData.description
      }
    ] as const) {
      if (validation.value === null || validation.value.trim().length === 0) {
        this.errors[validation.name] = 'This field is required';
      } else {
        this.errors[validation.name] = null;
      }
    }

    if (!this.quality.some((q) => q.value === this.viewData.quality)) {
      this.errors.quality = 'This field is required';
    } else {
      this.errors.quality = null;
    }

    if (
      Number.isNaN(this.viewData.viewerRating) ||
      !Number.isFinite(this.viewData.viewerRating) ||
      !Number.isInteger(this.viewData.viewerRating)
    ) {
      this.errors.viewerRating = 'Viewer rating should be a valid number';
    } else if (this.viewData.viewerRating < 1) {
      this.errors.viewerRating = 'Viewer rating should be greater than 0';
    } else {
      this.errors.viewerRating = null;
    }

    /**
     * Clears "runs indefinitely" error to avoid keeping previous errors
     */
    this.errors.runsIndefinitely = null;

    /**
     * Finally update parent component
     */
    if(this.viewDataUpdateEvent)
      this.viewDataUpdateEvent.emit(this.viewData);
    if(this.errorUpdateEvent)
      this.errorUpdateEvent.emit(this.errors);
  }

  public onChangeTitleThumbnail(
    thumbnail: IEpisodeThumbnailInputViewData | null
  ) {
    this.thumbnailTitleUpdateEvent.emit(true);
    this.viewData.thumbnails.title = thumbnail;
    this.onViewDataChange();
  }

  public onChangeShowThumbnail(
    thumbnail: IEpisodeThumbnailInputViewData | null
  ) {
    this.thumbnailCardUpdateEvent.emit(true);
    this.viewData.thumbnails.show = thumbnail;
    this.onViewDataChange();
  }

  toggleClosedCaptions(event){
    this.viewData.showDetails.closedCaptions = event.checked;
  }

  onChangeExtraDetails(index){
    this.extraDetailsList[index].value = !this.extraDetailsList[index].value;
  }

  onAnyExtraDetail(){
    let result  = false;
    this.extraDetailsList.forEach((detail) => {
      if(detail.value){
        result = true;
      }
    });
    return result;
  }

  onExtraDetailLabelActive(label:string){
    let result  = false;
    this.extraDetailsList.forEach((detail) => {
      if(detail.label === label && detail.value){
        result = true;
      }
    });
    return result;
  }

  onChangeExtraDetailsByLabel(label:string){
    this.extraDetailsList.forEach((detail) => {
      if(detail.label === label){
        detail.value = !detail.value;
      }
    });
  }
 
}
